import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Zap } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { cn } from '../../utils/cn'

export function useSubscriptionStatus(planExpiresAt: string | null | undefined) {
  if (!planExpiresAt) return { daysLeft: null, isExpired: false, isExpiringSoon: false, isExpiring30: false }
  const expiry = new Date(planExpiresAt)
  const now = new Date()
  const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / 86400000)
  return {
    daysLeft,
    isExpired: daysLeft <= 0,
    isExpiringSoon: daysLeft <= 7 && daysLeft > 0,
    isExpiring30: daysLeft <= 30 && daysLeft > 0,
  }
}

/**
 * Whether the tenant's access has genuinely lapsed (billing period end in the
 * past). Reads the SAME resolved source as the countdown badge so the two can
 * never diverge: the freshly-polled subscription period end (checkSubscriptionStatus
 * refreshes it every 60s), falling back to the mig-302-mirrored tenants.plan_expires_at
 * only when no subscription row has loaded yet. The persisted tenant.plan_expires_at
 * alone is unreliable — it is cached in localStorage and can be stale after an
 * upgrade, which is exactly what fired this banner for an active Pro tenant.
 */
export function useAccessLapsed(): { lapsed: boolean; isTrial: boolean } {
  const { subscription, tenant } = useAuthStore()
  const expiresAt = subscription?.current_period_end ?? tenant?.plan_expires_at ?? null
  if (!expiresAt) return { lapsed: false, isTrial: false }
  const lapsed = new Date(expiresAt).getTime() <= Date.now()
  // Trial vs paid decides the wording. subscription.status is authoritative; with
  // no subscription row yet, treat it as a trial (tenant.plan is only ever a paid
  // tier, so it cannot distinguish a lapsed trial here).
  const isTrial = subscription ? subscription.status === 'trialing' : true
  return { lapsed, isTrial }
}

function getCountdown(expiresAt: string) {
  const msLeft = new Date(expiresAt).getTime() - Date.now()
  if (msLeft <= 0) return null
  const totalSecs = Math.floor(msLeft / 1000)
  return {
    days: Math.floor(totalSecs / 86400),
    hours: Math.floor((totalSecs % 86400) / 3600),
    minutes: Math.floor((totalSecs % 3600) / 60),
    seconds: totalSecs % 60,
  }
}

export function TrialCountdownBadge() {
  const { tenant, isReadOnly, subscription } = useAuthStore()
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const id = setInterval(() => forceUpdate(n => n + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // Single source of truth: authStore.subscription (set by checkSubscriptionStatus polling)
  // Fall back to tenant.plan_expires_at only for the brief window before first poll resolves
  const expiresAt = subscription?.current_period_end ?? tenant?.plan_expires_at
  if (!expiresAt) return null

  const isTrialing = !subscription || subscription.status === 'trialing'
  const cd = getCountdown(expiresAt)

  // isReadOnly is the single authority on whether the trial has expired
  if (isReadOnly && isTrialing) {
    return (
      <Link
        to="/settings/subscription"
        className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
      >
        <Zap className="h-3 w-3" />
        Trial expired
      </Link>
    )
  }

  if (!cd) return null

  const { days, hours, minutes, seconds } = cd
  const planName = tenant?.plan
    ? tenant.plan.charAt(0).toUpperCase() + tenant.plan.slice(1)
    : 'Plan'

  const isRed = days < 3
  const isAmber = days >= 3 && days <= 7

  const pad = (n: number) => String(n).padStart(2, '0')
  const label = isTrialing
    ? `Trial • ${days}d ${hours}h ${minutes}m ${pad(seconds)}s`
    : `${planName} • [${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}] to renewal`

  return (
    <Link
      to="/settings/subscription"
      className={cn(
        'hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors',
        isRed
          ? 'bg-red-100 text-red-700 hover:bg-red-200'
          : isAmber
          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
          : 'bg-sage-100 text-sage-700 hover:bg-sage-200',
      )}
    >
      <Zap className="h-3 w-3" />
      {label}
    </Link>
  )
}
